import Vue from "vue";
import Vuex from "vuex";
import apiService from "@/services/ApiService.js";
import persistentLocale from "@/store/persistent-locale.js";
import { Mutations, Actions } from "@/utils/constants/types.js";
import constants from "@/utils/constants.js";
import i18n from "@/i18n.js";
import { shuffle } from "lodash";

Vue.use(Vuex);

export default new Vuex.Store({
  plugins: [persistentLocale],
  state: {
    appId: 0,
    locale: "nl",
    openMenuDrawer: false,
    sidebarDrawer: {
      active: false,
      mode: null
    },
    showAppsModal: false,
    panoramaError: null,
    hotspots: [],
    hotspot: {},
    hotspotLayer: {},
    hotspotPois: [],
    timeLayer: {},
    timeLayers: [],
    themes: [],
    theme: {},
    routes: [],
    route: {},
    search5D: [],
    selectedArticle: {},
    selectedArticleComments: [],
    articleDrawer: false,
    articleStack: [],
    featuredArticles: [],
    showFeaturedArticles: false
  },
  getters: {
    getAppId: state => state.appId,
    getAppName: state => {
      if (state.appId == 0) return "";
      if (state.appId == 1) return constants.HUYGENS;
      if (state.appId == 2) return constants.DORDRECHT5D;
      if (state.appId == 3) return constants.CANALS5D;
    },
    getArticleDrawer: state => state.articleDrawer,
    getLocale: state => state.locale,
    getMapPois: state => state.theme?.map_pois,
    getRoutePois: state => state.route?.route_pois
  },
  mutations: {
    [Mutations.SET_APP_ID]: (state, location) => {
      if (location == constants.HUYGENS_URL) {
        state.appId = 1;
      }
      if (location == constants.DORDRECHT5D_URL) {
        state.appId = 2;
      }
      if (location == constants.CANALS5D_URL) {
        state.appId = 3;
      }
    },
    [Mutations.SHOW_APPS_MODAL]: (state, showAppsModal) => {
      state.showAppsModal = showAppsModal;
    },
    [Mutations.SET_PANORAMA_ERROR]: (state, panoramaError) => {
      state.panoramaError = panoramaError;
    },
    [Mutations.TOGGLE_MENU_DRAWER]: state => {
      state.openMenuDrawer = !state.openMenuDrawer;
      //Close sidebar drawer
      if (state.sidebarDrawer.active) {
        state.sidebarDrawer.active = false;
      }
      if (state.articleDrawer) {
        state.articleDrawer = false;
      }

      state.showFeaturedArticles = false;
    },
    [Mutations.TOGGLE_ARTICLE_DRAWER]: (state, drawerState) => {
      if (!state.articleDrawer) {
        state.articleDrawer = drawerState;
      }
    },
    [Mutations.SET_ARTICLE]: (state, article) => {
      state.selectedArticle = article;
      state.selectedArticleComments = article.comments;

      let hasArticle = state.articleStack.find(a => a.id == article.id);
      if (!hasArticle) {
        state.articleStack.push(article);
      }

      if (state.articleDrawer != true) {
        state.articleDrawer = true;
      }
    },
    [Mutations.ADD_ARTICLE_STACK]: (state, article) => {
      let hasArticle = state.articleStack.find(a => a.id == article.id);
      if (!hasArticle) {
        state.articleStack.push(article);
      }
    },
    [Mutations.REMOVE_ARTICLE_STACK]: (state, id) => {
      let articles = state.articleStack.filter(article => article.id != id);
      state.articleStack = articles;
    },
    [Mutations.CLEAR_ARTICLE_STACK]: state => {
      state.articleStack = [];
      state.articleDrawer = false;
    },
    [Mutations.SET_LOCALE]: (state, locale) => {
      state.locale = locale;
      i18n.locale = locale;
    },
    [Mutations.SET_HOTSPOTS]: (state, data) => {
      state.hotspots = data;
    },
    [Mutations.SET_HOTSPOT]: (state, data) => {
      state.hotspot = data;
    },
    [Mutations.SET_TIMELAYER]: (state, data) => {
      state.timeLayer = data;
    },
    [Mutations.SET_TIMELAYERS]: (state, data) => {
      state.timeLayers = data;
    },
    [Mutations.SET_HOTSPOT_POIS]: (state, data) => {
      state.hotspotPois = data;
    },
    [Mutations.SET_HOTSPOT_LAYER]: (state, data) => {
      state.hotspotLayer = data;
    },
    [Mutations.TOGGLE_MENU]: (state, menu) => {
      if (!state[menu.name]) {
        state[menu.name] = !state[menu.name];
      }
    },
    [Mutations.TOGGLE_SIDEBAR_MENU]: (state, menu) => {
      //Close header menu
      if (state.openMenuDrawer) {
        state.openMenuDrawer = false;
      }
      //Toggle sidebar menu
      if (menu.mode != state.sidebarDrawer.mode) {
        state.sidebarDrawer.active = true;
      } else {
        state.sidebarDrawer.active = !state.sidebarDrawer.active;
      }
      //Set new drawer mode
      state.sidebarDrawer.mode = menu?.mode ?? state.sidebarDrawer.mode;
    },
    [Mutations.RESET_SIDEBAR_MENU]: state => {
      state.sidebarDrawer = {
        active: false,
        mode: null
      };
    },
    [Mutations.SET_THEMES]: (state, data) => {
      state.themes = data;
    },
    [Mutations.SET_SELECTED_THEME]: (state, data) => {
      state.theme = data;
    },
    [Mutations.RESET_THEME]: state => {
      state.theme = {};
    },
    [Mutations.RESET_THEMES]: state => {
      state.themes = [];
    },
    [Mutations.SET_ROUTES]: (state, data) => {
      state.routes = data;
    },
    [Mutations.SET_SELECTED_ROUTE]: (state, data) => {
      state.route = data;
    },
    [Mutations.RESET_ROUTE]: state => {
      state.route = {};
    },
    [Mutations.RESET_ROUTES]: state => {
      state.routes = [];
    },
    [Mutations.SET_5D_SEARCH]: (state, data) => {
      state.search5D = data;
    },
    [Mutations.REMOVE_5D_SEARCH]: state => {
      state.search5D = [];
    },
    [Mutations.SET_FEATURED_ARTICLES]: (state, featuredArticles) => {
      state.featuredArticles = shuffle(featuredArticles);
    },
    [Mutations.SHOW_FEATURED_ARTICLES]: (state, show) => {
      state.showFeaturedArticles = show;
    }
  },
  actions: {
    async [Actions.GET_THEMES]({ commit }) {
      const result = await apiService.themes.getThemes();
      commit(Mutations.SET_THEMES, result?.data?.data);
      return result;
    },

    [Actions.SELECT_THEME]({ commit }, theme) {
      commit(Mutations.RESET_ROUTE);
      commit(Mutations.SET_SELECTED_THEME, theme);
    },

    async [Actions.GET_ROUTES]({ commit }) {
      const result = await apiService.routes.getRoutes();
      commit(Mutations.SET_ROUTES, result?.data?.data);
      return result;
    },

    [Actions.SELECT_ROUTE]({ commit }, route) {
      commit(Mutations.RESET_THEME);
      commit(Mutations.SET_SELECTED_ROUTE, route);
    },

    async [Actions.GET_HOTSPOTS]({ commit }) {
      const result = await apiService.hotspots.getHotspots();
      commit(Mutations.SET_HOTSPOTS, result?.data?.data);
      return result;
    },
    async [Actions.GET_HOTSPOT]({ commit }, id) {
      const result = await apiService.hotspots.getHotspot(id);

      const layers =
        result?.data?.data?.hotspot_layers != null
          ? result?.data?.data?.hotspot_layers
          : [];

      const orderdLayers = layers.sort(function(first, second) {
        return first.year < second.year;
      });

      commit(Mutations.SET_HOTSPOT, result?.data?.data);
      commit(
        Mutations.SET_HOTSPOT_LAYER,
        orderdLayers.filter(layer => !layer.expert)[0]
      );

      //Get first hotspotlayer from hotspot
      const layer =
        result?.data?.data?.hotspot_layers.length > 0
          ? orderdLayers.filter(layer => !layer.expert)[0]
          : [];

      commit(Mutations.SET_HOTSPOT_POIS, layer?.hotspot_layer_pois);
      commit(Mutations.SET_TIMELAYER, layer);
      commit(Mutations.SET_TIMELAYERS, orderdLayers);
      return result;
    },

    async [Actions.GET_5D_SEARCH]({ commit }, term) {
      const result = await apiService.search.getSearch(term);
      commit(Mutations.SET_5D_SEARCH, result?.data?.data);
      return result;
    },

    //ARTICLES
    async [Actions.GET_ARTICLE]({ commit }, article_id) {
      const result = await apiService.articles.getArticle(article_id);
      commit(Mutations.SET_ARTICLE, result?.data?.data);
      return result;
    },

    async [Actions.ADD_ARTICLE]({ commit }, article_id) {
      const result = await apiService.articles.getArticle(article_id);
      // commit(Mutations.ADD_ARTICLE_STACK, result?.data?.data);
      return result?.data?.data;
    },

    //COMMENTS
    async [Actions.ADD_COMMENT](_context, payload) {
      const result = await apiService.comments.addComment(payload);
      this.state.selectedArticleComments.unshift(payload);
      return result;
    },

    //FEATURED ARTICLES
    async [Actions.GET_FEATURED_ARTICLES]({ commit }) {
      const result = await apiService.featuredArticles.getFeaturedArticles();
      commit(Mutations.SET_FEATURED_ARTICLES, result?.data?.data);
      return result?.data?.data;
    }
  }
});
